// ScanProgress.jsx — live batch-scan progress panel for the queue rail
// (design_handoff_attachment_field_scan, scan-v2-progress).
//
// One row per record in the kickoff scope: gray spinner while queued, a
// 3px progress bar + % while actively scanning (2 concurrent), a green
// check when done. Per-record percent is cosmetic — the OCR call reports
// no granular progress, so it creeps while a record scans and snaps to
// 100 when its status lands. The list auto-scrolls to keep the active
// record visible.

import { useEffect, useRef, useState } from 'react';
import { T } from '../../theme';
import { Meter } from '../fmxkit';

const ROW = 34;

export default function ScanProgressPanel({ scan }) {
  const { records = [], status = {}, done = 0, total = 0, errors = {} } = scan || {};
  const finished = (id) => status[id] === 'done' || status[id] === 'error';

  const [pcts, setPcts] = useState({});
  const statusRef = useRef(status);
  statusRef.current = status;
  useEffect(() => {
    const t = setInterval(() => {
      setPcts(p => {
        const st = statusRef.current;
        let changed = false;
        const next = { ...p };
        for (const id of Object.keys(st)) {
          if (st[id] !== 'scanning') continue;
          next[id] = Math.min(92, (next[id] || 0) + 3 + Math.random() * 6);
          changed = true;
        }
        return changed ? next : p;
      });
    }, 220);
    return () => clearInterval(t);
  }, []);

  const pctOf = (id) => (finished(id) ? 100 : status[id] === 'scanning' ? (pcts[id] || 0) : 0);
  const overall = total ? records.reduce((a, r) => a + pctOf(r.id), 0) / total : 0;

  // Keep the actively-scanning record in view.
  const listRef = useRef(null);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const activeIdx = records.findIndex(r => !finished(r.id));
    const target = activeIdx < 0
      ? el.scrollHeight
      : Math.max(0, activeIdx * ROW - el.clientHeight + ROW * 2);
    if (Math.abs(el.scrollTop - target) > 4) el.scrollTo({ top: target, behavior: 'smooth' });
  }, [done]); // re-follow whenever a record finishes

  return (
    <div style={{ border: `1px solid ${T.blue200}`, borderRadius: T.radiusTable, background: T.white, overflow: 'hidden' }}>
      <div style={{ padding: '8px 10px 9px', background: T.blue50, borderBottom: `1px solid ${T.blue200}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 11, color: T.brand }} />
          <span style={{ font: `600 12px/16px ${T.fontSans}`, color: T.ink }}>Scanning {total} equipment…</span>
          <span style={{ marginLeft: 'auto', font: `500 11px/15px ${T.fontSans}`, color: T.inkMuted }}>{done} of {total}</span>
        </div>
        <Meter pct={overall} h={4} fill={T.brand} />
      </div>
      <div ref={listRef} style={{ maxHeight: 218, overflowY: 'auto' }}>
        {records.map((r, i) => {
          const st = status[r.id];
          const pct = pctOf(r.id);
          return (
            <div className="scan-prog-row" key={r.id} style={{
              height: ROW, display: 'flex', alignItems: 'center', gap: 9, padding: '0 10px',
              boxSizing: 'border-box', borderBottom: i === records.length - 1 ? 'none' : `1px solid ${T.borderSubtle}`,
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, minWidth: 0 }}>
                  <span style={{ font: `600 12px/16px ${T.fontSans}`, color: T.ink, whiteSpace: 'nowrap' }}>{r.tag}</span>
                  <span style={{ font: `400 11px/15px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {[r.type, r.bld].filter(s => s && s !== '—').join(' · ')}
                  </span>
                </div>
                {st === 'scanning' && (
                  <div style={{ marginTop: 3 }}>
                    <Meter pct={pct} h={3} fill={T.brand} />
                  </div>
                )}
              </div>
              {st === 'done'
                ? <i className="fa-solid fa-circle-check" style={{ fontSize: 12, color: T.green500, flexShrink: 0 }} />
                : st === 'error'
                  ? <i className="fa-solid fa-triangle-exclamation" title={`${errors[r.id] || 'Scan failed'} — the record stays unscanned and can be retried on the next kickoff`} style={{ fontSize: 12, color: T.red500, flexShrink: 0 }} />
                  : st === 'scanning'
                    ? <span style={{ font: `600 10.5px/14px ${T.fontSans}`, color: T.brand, flexShrink: 0 }}>{Math.round(pct)}%</span>
                    : <i className="fa-solid fa-circle-notch fa-spin" style={{ fontSize: 11, color: T.inkSoft, flexShrink: 0 }} />}
            </div>
          );
        })}
      </div>
      <div style={{ padding: '6px 10px', borderTop: `1px solid ${T.borderSubtle}`, font: `400 10.5px/14px ${T.fontSans}`, color: T.inkSoft }}>
        Results land in the review queue as each record finishes.
      </div>
    </div>
  );
}
